import React from 'react';
import { updateAssignedUserUploadStatus } from '../../services/requirementService';
import { showSuccess, showError } from '../../utils/toast';
import { getApiErrorMessage } from '../../utils/apiError';

export function getManualUploadActive(user = {}) {
  if (typeof user.manualProfileUploadEnabled === 'boolean') {
    return user.manualProfileUploadEnabled;
  }
  return user.profileUploadEnabled !== false;
}

/** Backend-sourced badge/action state (employee + requirement). Falls back for legacy payloads. */
export function getUploadDisplayState(entity = {}) {
  const hasBackendShape = typeof entity.effectiveProfileUploadEnabled === 'boolean'
    || typeof entity.currentlyAssigned === 'boolean';

  if (hasBackendShape) {
    const badgeActive = typeof entity.effectiveProfileUploadEnabled === 'boolean'
      ? entity.effectiveProfileUploadEnabled
      : entity.profileUploadEnabled !== false && entity.currentlyAssigned !== false;
    const buttonAction = entity.uploadAction === 'start' ? 'start' : 'stop';
    const canToggle = entity.currentlyAssigned !== false;
    const toggleManualActive = typeof entity.manualProfileUploadEnabled === 'boolean'
      ? entity.manualProfileUploadEnabled
      : getManualUploadActive(entity);
    return { badgeActive, buttonAction, canToggle, toggleManualActive };
  }

  const toggleManualActive = getManualUploadActive(entity);
  return {
    badgeActive: toggleManualActive,
    buttonAction: toggleManualActive ? 'stop' : 'start',
    canToggle: true,
    toggleManualActive,
  };
}

export function UploadStatusBadge({ isManuallyActive }) {
  if (isManuallyActive) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '6px 12px',
          borderRadius: '20px',
          fontWeight: 'bold',
          color: '#155724',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
        }}
      >
        🟢 Uploading Enabled
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '6px 12px',
        borderRadius: '20px',
        fontWeight: 'bold',
        color: '#721c24',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
      }}
    >
      🔴 Uploading Stopped
    </span>
  );
}

export async function confirmAndToggleAssignedUserUploadStatus(
  requirementId,
  userId,
  isManuallyActive,
  onSuccess,
) {
  if (!requirementId || !userId) {
    showError('Unable to update profile upload status.');
    return false;
  }

  const nextManualState = !isManuallyActive;
  const isConfirmed = window.confirm(
    isManuallyActive
      ? 'Stop profile uploading for this user for this requirement?'
      : 'Start profile uploading for this user for this requirement?',
  );

  if (!isConfirmed) return false;

  try {
    const result = await updateAssignedUserUploadStatus(requirementId, userId, nextManualState);

    if (result.status === 'Success') {
      showSuccess(isManuallyActive ? 'Profile upload stopped for this user.' : 'Profile upload started for this user.');
      if (typeof onSuccess === 'function') {
        await onSuccess(result);
      }
      return true;
    }

    showError(result.msg || 'Unable to update profile upload status.');
    return false;
  } catch (error) {
    console.error('Error updating profile upload status:', error);
    showError(getApiErrorMessage(error, 'Unable to update profile upload status.'));
    return false;
  }
}

export function UploadActionButton({
  isManuallyActive,
  buttonAction,
  disabled = false,
  onClick,
}) {
  const action = buttonAction ?? (isManuallyActive ? 'stop' : 'start');
  const showStop = action === 'stop';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.backgroundColor = showStop ? '#f0ad4e' : '#5cb85c';
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.backgroundColor = showStop ? '#ffc107' : 'lightgreen';
      }}
      style={{
        borderRadius: '20px',
        fontWeight: 'bolder',
        color: 'black',
        border: '0px',
        padding: '6px 16px',
        backgroundColor: showStop ? '#ffc107' : 'lightgreen',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {showStop ? 'Stop' : 'Start'}
    </button>
  );
}
